import React, { useRef, useState } from "react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import { canvasPreview } from "./canvasPreview";
import { useDebounceEffect } from "./useDebounceEffect";
import { Button } from "@mui/material";
import { PageContainer } from "../../../../components/PageContainer";
import { EditImageContainer } from "../Select";
import { Note } from "../../../../components/Note";
interface ICropProps {
  file: string;
  sourceFileType?: string;
  handleCroppedFile: (file: string, aspect: number) => void;
}
//https://codesandbox.io/s/react-image-crop-demo-with-react-hooks-y831o?file=/src/App.tsx:1967-1983

//numbers from iphone 15 pro
// const aspectRatios = [
//   393 / 180, // news list
//   393 / 250, // news item
//   16 / 9,
//   1 / 1,
// ];

const aspectRatios = {
  UP_NEXT: {
    width: 255.3,
    height: 103,
  },
  ARTIST_PROFILE: {
    width: 393,
    height: 250,
  },
  LINKED_ITEM: {
    width: 353.7,
    height: 185,
  },
};

const CropImage = ({
  file,
  sourceFileType,
  handleCroppedFile,
}: ICropProps) => {
  const [imgSrc, setImgSrc] = useState(file);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const upNextPreviewRef = useRef<HTMLCanvasElement>(null);
  const artistProfilePreview = useRef<HTMLCanvasElement>(null);
  const linkedItemPreview = useRef<HTMLCanvasElement>(null);

  const imgRef = useRef<HTMLImageElement>(null);
  const hiddenAnchorRef = useRef<HTMLAnchorElement>(null);
  const blobUrlRef = useRef("");
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState<number | undefined>(16 / 9);

  const cropRef = useRef<ReactCrop>(null);

  const imagePreviews = [
    {
      name: `"Up Next" swiper:`,
      description: "Appears on the home screen during the event, within a swipable list of 10 items",
      ref: upNextPreviewRef,
      size: aspectRatios.UP_NEXT,
    },
    {
      name: `Artist/Vendor profile`,
      description: "The primary image on an artist's bio page",
      ref: artistProfilePreview,
      size: aspectRatios.ARTIST_PROFILE,
    },
    {
      name: `Linked Item`,
      description: "Appears on an artist or vendor bio, under the description",
      ref: linkedItemPreview,
      size: aspectRatios.LINKED_ITEM,
    },
  ];

  //   function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
  //     if (e.target.files && e.target.files.length > 0) {
  //       setCrop(undefined); // Makes crop preview update between images.
  //       const reader = new FileReader();
  //       reader.addEventListener("load", () =>
  //         setImgSrc(reader.result?.toString() || "")
  //       );
  //       reader.readAsDataURL(e.target.files[0]);
  //     }
  //   }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  function onContinue() {
    if (!previewCanvasRef.current) {
      throw new Error("Crop canvas does not exist");
    }

    // Determine the original image format
    let imageType = "image/jpeg"; // Default
    let imageQuality = 0.8;
    const normalizedSourceFileType = sourceFileType?.toLowerCase();

    // Use selected file mime type when available (e.g. PNG with transparency).
    if (normalizedSourceFileType === "image/jpg") {
      imageType = "image/jpeg";
    } else if (
      normalizedSourceFileType &&
      ["image/png", "image/jpeg", "image/webp"].includes(
        normalizedSourceFileType
      )
    ) {
      imageType = normalizedSourceFileType;
    }
    // Check for common image formats in the data URL
    else if (imgSrc.startsWith("data:")) {
      const mimeMatch = imgSrc.match(/^data:([^;]+);/);
      if (mimeMatch && mimeMatch[1]) {
        imageType =
          mimeMatch[1].toLowerCase() === "image/jpg"
            ? "image/jpeg"
            : mimeMatch[1];
      }
    } else if (imgSrc.startsWith("blob:")) {
      // For blob URLs, we can't easily determine the type
      // We'll use file extension if available
      if (imgSrc.toLowerCase().endsWith(".png")) {
        imageType = "image/png";
      } else if (imgSrc.toLowerCase().endsWith(".gif")) {
        imageType = "image/gif";
      } else if (imgSrc.toLowerCase().endsWith(".webp")) {
        imageType = "image/webp";
      }
    }

    // Adjust quality based on format
    if (imageType === "image/png") {
      imageQuality = 0.9;
    } else if (imageType === "image/webp") {
      imageQuality = 0.85;
    }

    // Some formats don't support quality parameter
    const supportsQuality = ["image/jpeg", "image/webp", "image/png"].includes(
      imageType
    );

    previewCanvasRef.current.toBlob(
      (blob) => {
        if (!blob) {
          throw new Error("Failed to create blob");
        }
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = URL.createObjectURL(blob);

        const { width, height } = completedCrop ?? { width: 0, height: 0 };

        const ratio = width / height;

        handleCroppedFile(
          blobUrlRef.current,
          parseFloat(ratio.toFixed(2)) ?? 16 / 9
        );
      },
      imageType,
      supportsQuality ? imageQuality : undefined
    );
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
          false,
          imagePreviews.map((item) => ({
            canvas: item.ref.current,
            size: item.size,
          }))
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const handleAspectRatioPress = (aspectRatio: number | undefined) => {
    // @ts-ignore
    const { width, height } = imgRef.current;

    setCrop(centerAspectCrop(width, height, aspectRatio ?? 1));
    setAspect(aspectRatio);
  };

  return (
    <PageContainer title="Crop the image">
      <EditImageContainer>
        <div
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "25% 25%",
            // gridGap: "20",
            gridColumnGap: "8px",
            alignItems: "center",
          }}
        >
          <div>
            <label htmlFor="scale-input">Scale: </label>
            <input
              id="scale-input"
              type="number"
              step="0.1"
              value={scale}
              disabled={!imgSrc}
              onChange={(e) => setScale(Number(e.target.value))}
            />
          </div>
          <div>
            <label htmlFor="rotate-input">Rotate: </label>
            <input
              id="rotate-input"
              type="number"
              value={rotate}
              disabled={!imgSrc}
              onChange={(e) =>
                setRotate(Math.min(180, Math.max(-180, Number(e.target.value))))
              }
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gridColumnStart: 1,
              gridColumnEnd: 4,
              gridRow: 2,
              paddingTop: 50,
            }}
          >
            <Button
              disabled={true}
              onClick={() => handleAspectRatioPress(2 / 1)}
              variant={aspect === 2 / 1 ? "contained" : "outlined"}
              style={{ flex: 1 }}
            >
              Aspect Ratio: 2/1
            </Button>
            <Button
              onClick={() => handleAspectRatioPress(16 / 9)}
              variant={aspect === 16 / 9 ? "contained" : "outlined"}
              style={{ flex: 1 }}
            >
              Aspect Ratio: 16 / 9
            </Button>
            <Button
              onClick={() => handleAspectRatioPress(1 / 1)}
              variant={aspect === 1 / 1 ? "contained" : "outlined"}
              style={{ flex: 1 }}
            >
              Aspect Ratio: 1/1
            </Button>
            <Button
              onClick={() => handleAspectRatioPress(undefined)}
              variant={aspect === undefined ? "contained" : "outlined"}
              style={{ flex: 1 }}
            >
              Free Form
            </Button>
          </div>
        </div>
        <div style={{ paddingTop: 30, paddingBottom: 30 }}>
          <Button variant="contained" onClick={onContinue}>
            Proceed
          </Button>
          <a
            ref={hiddenAnchorRef}
            download
            style={{
              position: "absolute",
              top: "-200vh",
              visibility: "hidden",
            }}
          >
            Hidden download
          </a>
        </div>
      </EditImageContainer>
      <Note
        message={`Info: A preview of the cropped image can be found below`}
        style={{ marginBottom: 38 }}
      />
      {!!imgSrc && (
        <ReactCrop
          ref={cropRef}
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspect}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      )}
      {!!completedCrop && (
        <>
          <div style={{ gap: 12, flexDirection: "row", flex: 1 }}>
            <canvas
              ref={previewCanvasRef}
              style={{
                // border: "20px dotted purple",
                objectFit: "cover",
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
            <h2>Previews:</h2>
            <p>
              These give an indication of how the image may look within
              different areas of the app.
            </p>
            {imagePreviews.map((item) => (
              <div>
                <h3>{item.name}</h3>
                <p>{item.description}</p>
                <canvas
                  ref={item.ref}
                  style={{
                    // border: "20px dotted purple",
                    objectFit: "cover",
                    width: item.size.width * 2,
                    height: item.size.height * 2,
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <Button variant="contained" onClick={onContinue}>
        Proceed
      </Button>
    </PageContainer>
  );
};

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default CropImage;
